import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

const RecursiveDataList = ({ data, level = 0 }) => {
  if (data === null || data === undefined) return <span className="rdl-value null">—</span>;
  
  // Primitive values
  if (typeof data !== 'object') {
    const isBool = typeof data === 'boolean';
    const val = String(data);
    let statusClass = '';
    const lower = val.toLowerCase();
    
    if (['healthy', 'ok', 'operational', 'true', 'active', 'online'].includes(lower)) statusClass = 'status-ok';
    else if (['degraded', 'warning', 'warn'].includes(lower)) statusClass = 'status-warn';
    else if (['error', 'fail', 'false', 'inactive', 'offline', 'down'].includes(lower)) statusClass = 'status-fail';
    
    return (
      <span className={`rdl-value-pill ${statusClass}`}>
        {val}
      </span>
    );
  }

  // Arrays
  if (Array.isArray(data)) {
    if (data.length === 0) return <span className="rdl-empty">Empty list</span>;
    return (
      <div className={`rdl-array level-${level}`}>
        {data.map((item, i) => (
          <div key={i} className="rdl-array-item">
            <RecursiveDataList data={item} level={level + 1} />
          </div>
        ))}
      </div>
    );
  }

  // Objects
  return (
    <div className={`rdl-object level-${level}`}>
      {Object.entries(data).map(([key, value]) => {
        const isComplex = value !== null && typeof value === 'object';
        const displayKey = key.replace(/_/g, ' ');
        
        return (
          <div key={key} className={`rdl-entry ${isComplex ? 'complex' : 'simple'}`}>
            <div className="rdl-header">
              <span className="rdl-key">{displayKey}</span>
              {!isComplex && (
                <div className="rdl-primitive-val">
                  <RecursiveDataList data={value} level={level + 1} />
                </div>
              )}
            </div>
            {isComplex && (
              <div className="rdl-body">
                <RecursiveDataList data={value} level={level + 1} />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const AdminSettings = ({
  firebase,
  serverHealth,
  organizations,
  projectsByOrg,
  documents,
  users,
  onRefreshHealth,
}) => {
  const { t } = useTranslation();
  const [refreshing, setRefreshing] = useState(false);
  const [health, setHealth] = useState(serverHealth);

  useEffect(() => {
    setHealth(serverHealth);
  }, [serverHealth]);

  const handleRefresh = async () => {
    setRefreshing(true);
    const result = await onRefreshHealth();
    setHealth(result);
    setRefreshing(false);
  };

  const totalProjects = useMemo(() =>
    Object.values(projectsByOrg || {}).reduce((s, p) => s + p.length, 0),
    [projectsByOrg]);

  const isHealthy = health?.healthy;
  const dbOk = health?.database === 'healthy';
  const diskOk = health?.diskStatus === 'healthy';

  return (
    <div className="settings-tab-content">
      <header className="settings-header">
        <h4>{t('admin.tab_settings')}</h4>
      </header>

      <div className="settings-grid">
        {/* ── Backend Health ─────────────────────────────────────────── */}
        <section className="settings-section glass">
          <div className="section-title-row">
            <h5>{t('admin.health.server_vitals')}</h5>
            <button
              className="outline contrast sm refresh-btn"
              onClick={handleRefresh}
              disabled={refreshing}
              aria-busy={refreshing}
            >
              {!refreshing && '↻ Refresh'}
            </button>
          </div>

          <div className="health-status-banner">
            <span className={`health-dot-lg ${isHealthy ? 'ok' : 'fail'}`} />
            <div>
              <strong>{isHealthy ? 'All Systems Operational' : 'Service Disruption'}</strong>
              {health?.message && <p className="health-message">{health.message}</p>}
            </div>
          </div>

          <div className="health-metrics-grid">
            <div className="health-metric">
              <span className="hm-label">{t('admin.health.latency')}</span>
              <span className={`hm-value ${health?.responseTime > 1000 ? 'warn' : ''}`}>
                {health?.responseTime != null ? `${health.responseTime}ms` : '—'}
              </span>
            </div>
            <div className="health-metric">
              <span className="hm-label">{t('admin.health.database')}</span>
              <span className={`hm-value ${dbOk ? '' : 'warn'}`}>
                {health?.database || '—'}
              </span>
            </div>
            <div className="health-metric">
              <span className="hm-label">{t('admin.health.disk_space')}</span>
              <span className={`hm-value ${health?.diskSpaceGb < 1 ? 'warn' : ''}`}>
                {health?.diskSpaceGb != null ? `${health.diskSpaceGb} GB` : '—'}
              </span>
            </div>
            <div className="health-metric">
              <span className="hm-label">{t('admin.health.disk_status')}</span>
              <span className={`hm-value ${diskOk ? '' : 'warn'}`}>
                {health?.diskStatus || '—'}
              </span>
            </div>
            <div className="health-metric">
              <span className="hm-label">Version</span>
              <span className="hm-value">
                {health?.version ? `v${health.version}` : '—'}
              </span>
            </div>
            <div className="health-metric">
              <span className="hm-label">Status</span>
              <span className="hm-value">
                {health?.status || '—'}
              </span>
            </div>
          </div>

          {health?.details && (
            <div className="health-details-expanded">
              <hr />
              <h6 className="details-header">Detailed System Health</h6>
              <div className="details-container-scroll">
                <RecursiveDataList data={health.details} />
              </div>
            </div>
          )}
        </section>

        {/* ── Platform Inventory ─────────────────────────────────────── */}
        <section className="settings-section glass">
          <h5>{t('admin.settings.platform_summary')}</h5>

          <div className="platform-stats-grid">
            <div className="platform-stat">
              <span className="ps-value">{organizations?.length || 0}</span>
              <span className="ps-label">{t('admin.stats.active_roles')}</span>
            </div>
            <div className="platform-stat">
              <span className="ps-value">{totalProjects}</span>
              <span className="ps-label">{t('admin.stats.total_projects')}</span>
            </div>
            <div className="platform-stat">
              <span className="ps-value">{documents?.length || 0}</span>
              <span className="ps-label">{t('admin.stats.total_documents')}</span>
            </div>
            <div className="platform-stat">
              <span className="ps-value">{users?.length || 0}</span>
              <span className="ps-label">{t('admin.stats.total_users')}</span>
            </div>
          </div>

          <div className="platform-info-list">
            <div className="platform-info-row">
              <span>Firebase Project</span>
              <code>{process.env.REACT_APP_FIREBASE_PROJECT_ID || 'N/A'}</code>
            </div>
            <div className="platform-info-row">
              <span>Auth Domain</span>
              <code>{process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || 'N/A'}</code>
            </div>
            <div className="platform-info-row">
              <span>Database Region</span>
              <code>europe-west1</code>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default AdminSettings;
